import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  SimpleGrid,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Input,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Heading,
  Th,
  Thead,
  Tr,
  VStack,
  Code,
} from "@chakra-ui/react";
import VmsPageCard from "../components/VmsPageCard";
import { vmsApi } from "../services/vmsApi";

const ACTION_LABELS = {
  AUTH_LOGIN_SUCCESS: "Logged in",
  AUTH_FORGOT_PASSWORD_REQUESTED: "Requested password reset",
  AUTH_RESET_PASSWORD_SUCCESS: "Reset password",
  AUTH_CHANGE_PASSWORD: "Changed password",
  ADMIN_SETUP_CREATED: "Admin setup created",
  ADMIN_CREATE_OFFICE_USER: "Created office user",
  ADMIN_BULK_CREATE_SECURITY_USERS: "Bulk created security users",
  ADMIN_UPDATE_SECURITY_USER_STATUS: "Updated security user status",
  ADMIN_DELETE_SECURITY_USER: "Deleted security user",
  ADMIN_UPDATE_OFFICE_USER_STATUS: "Updated office user status",
  ADMIN_DELETE_OFFICE_USER: "Deleted office user",
  ADMIN_CREATE_OFFICE: "Created office",
  ADMIN_CREATE_GATE: "Created gate",
  ADMIN_DELETE_GATE: "Deleted gate",
  ADMIN_CREATE_OFFICE_PERSON: "Created office person",
  ADMIN_UPDATE_OFFICE_PERSON: "Updated office person",
  ADMIN_DELETE_OFFICE_PERSON: "Deleted office person",
  GENERATE_BOOKING_LINK: "Generated booking link",
  ADMIN_RUN_DAILY_MAINTENANCE: "Ran daily maintenance",
  VISITOR_SIGN_IN_REQUEST_CREATED: "Visitor sign-in request created",
  REQUEST_APPROVE: "Approved request",
  REQUEST_DECLINE: "Declined request",
  REQUEST_RESCHEDULE: "Rescheduled request",
  REQUEST_REROUTE: "Rerouted request",
  VISITOR_SIGN_OUT: "Visitor signed out",
};

const ACTION_OPTIONS = Object.keys(ACTION_LABELS);

const STATUS_ICON = {
  REQUEST_APPROVE: "✅",
  REQUEST_DECLINE: "❌",
  REQUEST_RESCHEDULE: "🗓️",
  REQUEST_REROUTE: "↪️",
  VISITOR_SIGN_IN_REQUEST_CREATED: "📝",
  VISITOR_SIGN_OUT: "🚶",
  GENERATE_BOOKING_LINK: "🔗",
  ADMIN_BULK_CREATE_SECURITY_USERS: "👮",
  ADMIN_CREATE_OFFICE_USER: "👥",
  ADMIN_CREATE_OFFICE: "🏢",
  ADMIN_CREATE_GATE: "🚪",
  AUTH_LOGIN_SUCCESS: "🟢",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatRelativeTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const now = Date.now();
  const diffMs = now - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
};

const toIsoIfProvided = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
};

const getActorName = (actor) => {
  if (!actor) return "System/Public";
  return actor.name || actor.email || "System/Public";
};

const getActorRole = (actor) => {
  if (!actor) return "system";
  return actor.role || "system";
};

const summarizeDetails = (action, details) => {
  if (!details || typeof details !== "object") return "-";
  if (action === "ADMIN_BULK_CREATE_SECURITY_USERS") {
    const created = details.created_count ?? details.created ?? "-";
    const processed = details.processed_count ?? details.processed ?? "-";
    return `Processed ${processed}, created ${created}`;
  }
  if (
    action === "REQUEST_APPROVE" ||
    action === "REQUEST_DECLINE" ||
    action === "REQUEST_RESCHEDULE" ||
    action === "REQUEST_REROUTE"
  ) {
    const office = details.office_name || details.office || "";
    const person = details.person_name || details.person || "";
    const sentEmail = details.send_visitor_email;
    const sentText = sentEmail === undefined ? "" : `, email: ${sentEmail ? "yes" : "no"}`;
    return [office, person].filter(Boolean).join(" / ") + sentText || "Request decision";
  }
  if (action === "VISITOR_SIGN_OUT") {
    return details.method ? `Method: ${details.method}` : "Visitor signed out";
  }
  if (action === "GENERATE_BOOKING_LINK") {
    const office = details.office_name || details.office || "-";
    return `Office: ${office}`;
  }
  const candidates = ["message", "note", "status", "result"];
  for (const key of candidates) {
    if (details[key]) return String(details[key]);
  }
  const firstEntry = Object.entries(details)[0];
  if (!firstEntry) return "-";
  return `${firstEntry[0]}: ${String(firstEntry[1])}`;
};

const getActivityTitle = (action) => ACTION_LABELS[action] || action || "Activity";

const getActivityDescription = (item) => {
  const actor = getActorName(item?.actor);
  const details = item?.details || {};
  if (item?.action === "VISITOR_SIGN_IN_REQUEST_CREATED") {
    const visitor = details.visitor_name || details.name || "a visitor";
    const office = details.office_name || details.office || "an office";
    return `${visitor} submitted a visitor request to ${office}.`;
  }
  if (item?.action === "REQUEST_APPROVE") {
    const visitor = details.visitor_name || "a visitor";
    return `${actor} approved ${visitor}'s visitor request.`;
  }
  if (item?.action === "REQUEST_DECLINE") {
    const visitor = details.visitor_name || "a visitor";
    return `${actor} declined ${visitor}'s visitor request.`;
  }
  if (item?.action === "REQUEST_RESCHEDULE") {
    return `${actor} rescheduled a visitor request.`;
  }
  if (item?.action === "VISITOR_SIGN_OUT") {
    const gate = details.gate_name || details.gate || "the gate";
    return `Visitor completed visit and exited through ${gate}.`;
  }
  if (item?.action === "GENERATE_BOOKING_LINK") {
    const office = details.office_name || details.office || "an office";
    return `A booking link was generated for ${office}.`;
  }
  return summarizeDetails(item?.action, details);
};

const VmsActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({
    actor_email: "",
    actor_role: "",
    action: "",
    start_date: "",
    end_date: "",
    page: 1,
    page_size: 20,
  });
  const [meta, setMeta] = useState({
    total_count: 0,
    total_pages: 0,
    page: 1,
    page_size: 20,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);

  const loadActivity = async (nextFilters = filters) => {
    setIsLoading(true);
    try {
      const params = {
        page: Math.max(1, Number(nextFilters.page) || 1),
        page_size: Math.min(200, Math.max(1, Number(nextFilters.page_size) || 20)),
      };
      if (nextFilters.actor_email) params.actor_email = nextFilters.actor_email.trim();
      if (nextFilters.actor_role) params.actor_role = nextFilters.actor_role;
      if (nextFilters.action) params.action = nextFilters.action;
      const startDate = toIsoIfProvided(nextFilters.start_date);
      const endDate = toIsoIfProvided(nextFilters.end_date);
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await vmsApi.getActivityLog(params);
      const list = Array.isArray(response?.activities) ? response.activities : [];
      setActivities(list);
      setMeta({
        total_count: Number(response?.total_count || 0),
        total_pages: Number(response?.total_pages || 0),
        page: Number(response?.page || params.page),
        page_size: Number(response?.page_size || params.page_size),
      });
      setFilters((prev) => ({
        ...prev,
        ...nextFilters,
        page: Number(response?.page || params.page),
        page_size: Number(response?.page_size || params.page_size),
      }));
    } catch {
      setActivities([]);
      setMeta((prev) => ({ ...prev, total_count: 0, total_pages: 0 }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadActivity().catch(() => {});
  }, []);

  useEffect(() => {
    if (!autoRefreshSeconds) return undefined;
    const intervalId = window.setInterval(() => {
      loadActivity({ ...filters }).catch(() => {});
    }, autoRefreshSeconds * 1000);
    return () => window.clearInterval(intervalId);
  }, [autoRefreshSeconds, filters]);

  const friendlyTotal = useMemo(() => Number(meta.total_count || 0), [meta.total_count]);

  const todayCount = useMemo(() => {
    const today = new Date();
    return activities.filter((item) => {
      const d = new Date(item.created_at);
      return (
        !Number.isNaN(d.getTime()) &&
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
      );
    }).length;
  }, [activities]);

  const visitorRequestsCount = useMemo(
    () =>
      activities.filter(
        (item) =>
          item.action === "VISITOR_SIGN_IN_REQUEST_CREATED" ||
          item.action === "REQUEST_APPROVE" ||
          item.action === "REQUEST_DECLINE" ||
          item.action === "REQUEST_RESCHEDULE" ||
          item.action === "REQUEST_REROUTE"
      ).length,
    [activities]
  );

  const approvalsCount = useMemo(
    () => activities.filter((item) => item.action === "REQUEST_APPROVE").length,
    [activities]
  );
  const checkInsCount = useMemo(
    () =>
      activities.filter(
        (item) =>
          item.action === "VISITOR_SIGN_IN_REQUEST_CREATED" ||
          item.action === "VISITOR_ARRIVED_AT_OFFICE" ||
          item.action === "VISITOR_CHECKED_IN"
      ).length,
    [activities]
  );
  const checkOutsCount = useMemo(
    () => activities.filter((item) => item.action === "VISITOR_SIGN_OUT").length,
    [activities]
  );

  return (
    <VStack align="stretch" spacing={5}>
      <VmsPageCard
        title="Activity Feed"
        description="Monitor all visitor, office, security and system activities across the Visitor Management System."
      >
        <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3}>
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
            <Text fontSize="xs" color="gray.500">
              Today Activities
            </Text>
            <Heading size="md" mt={1}>
              {todayCount}
            </Heading>
          </Box>
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
            <Text fontSize="xs" color="gray.500">
              Visitor Requests
            </Text>
            <Heading size="md" mt={1}>
              {visitorRequestsCount}
            </Heading>
          </Box>
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
            <Text fontSize="xs" color="gray.500">
              Approvals
            </Text>
            <Heading size="md" mt={1}>
              {approvalsCount}
            </Heading>
          </Box>
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
            <Text fontSize="xs" color="gray.500">
              Check-ins
            </Text>
            <Heading size="md" mt={1}>
              {checkInsCount}
            </Heading>
          </Box>
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
            <Text fontSize="xs" color="gray.500">
              Check-outs
            </Text>
            <Heading size="md" mt={1}>
              {checkOutsCount}
            </Heading>
          </Box>
        </SimpleGrid>

        <HStack spacing={3} flexWrap="wrap" align="center">
          <Input
            w={{ base: "100%", md: "240px" }}
            placeholder="Search Activity (actor email)"
            value={filters.actor_email}
            onChange={(e) => setFilters((prev) => ({ ...prev, actor_email: e.target.value }))}
          />
          <Select
            w={{ base: "100%", md: "180px" }}
            value={filters.actor_role}
            onChange={(e) => setFilters((prev) => ({ ...prev, actor_role: e.target.value }))}
          >
            <option value="">Role</option>
            <option value="general_admin">general_admin</option>
            <option value="office_user">office_user</option>
            <option value="vms_security">vms_security</option>
            <option value="system">system</option>
          </Select>
          <Select
            w={{ base: "100%", md: "240px" }}
            value={filters.action}
            onChange={(e) => setFilters((prev) => ({ ...prev, action: e.target.value }))}
          >
            <option value="">Activity Type</option>
            {ACTION_OPTIONS.map((actionCode) => (
              <option key={actionCode} value={actionCode}>
                {ACTION_LABELS[actionCode]}
              </option>
            ))}
          </Select>
          <Input
            type="date"
            w={{ base: "100%", md: "170px" }}
            placeholder="Date Range"
            value={filters.start_date}
            onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
          />
          <Input
            type="date"
            w={{ base: "100%", md: "170px" }}
            value={filters.end_date}
            onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
          />
          <Input w={{ base: "100%", md: "200px" }} placeholder="Office (coming soon)" isDisabled />
          <Button colorScheme="green" onClick={() => loadActivity({ ...filters, page: 1 })} isLoading={isLoading}>
            Apply Filter
          </Button>
          <Button variant="outline" onClick={() => loadActivity({ ...filters })} isLoading={isLoading}>
            Refresh
          </Button>
          <Button variant="outline" isDisabled>
            Export
          </Button>
        </HStack>

        <HStack justify="space-between" mt={2}>
          <Text fontSize="sm" color="gray.600">
            Total activities: {friendlyTotal}
          </Text>
          <HStack flexWrap="wrap">
            <Text fontSize="sm" color="gray.600">
              Auto refresh
            </Text>
            <Select
              size="sm"
              w="110px"
              value={String(autoRefreshSeconds)}
              onChange={(e) => setAutoRefreshSeconds(Number(e.target.value || 0))}
            >
              <option value="0">Off</option>
              <option value="30">30s</option>
              <option value="60">60s</option>
            </Select>
            <Text fontSize="sm" color="gray.600">
              Page size
            </Text>
            <Select
              size="sm"
              w="92px"
              value={String(filters.page_size)}
              onChange={(e) => setFilters((prev) => ({ ...prev, page_size: Number(e.target.value || 20) }))}
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </Select>
          </HStack>
        </HStack>

        <Box>
          <Text fontWeight="700" color="gray.700" mb={2}>
            Activity Timeline
          </Text>
        </Box>

        <TableContainer border="1px solid" borderColor="gray.200" borderRadius="12px">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Activity Timeline (newest first)</Th>
              </Tr>
            </Thead>
            <Tbody>
              {activities.map((item) => (
                <Tr
                  key={item.id || `${item.created_at}-${item.action}`}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => setSelectedItem(item)}
                >
                  <Td>
                    <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={3}>
                      <HStack justify="space-between" align="start">
                        <HStack align="start">
                          <Text fontSize="lg">{STATUS_ICON[item.action] || "📌"}</Text>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="700">{getActivityTitle(item.action)}</Text>
                            <Text color="gray.700">{getActivityDescription(item)}</Text>
                            <Text fontSize="sm" color="gray.500">
                              By: {getActorName(item.actor)} ({getActorRole(item.actor)})
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {formatRelativeTime(item.created_at)} • {formatDateTime(item.created_at)}
                            </Text>
                          </VStack>
                        </HStack>
                        <Badge>{ACTION_LABELS[item.action] || item.action || "-"}</Badge>
                      </HStack>
                    </Box>
                  </Td>
                </Tr>
              ))}
              {!isLoading && !activities.length ? (
                <Tr>
                  <Td>
                    <Text py={8} textAlign="center" color="gray.500">
                      No activities found for current filters.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </TableContainer>

        <HStack mt={2} justify="center" spacing={4}>
          <Button
            variant="ghost"
            onClick={() => loadActivity({ ...filters, page: Math.max(1, Number(meta.page || 1) - 1) })}
            isDisabled={Number(meta.page || 1) <= 1 || isLoading}
          >
            Previous
          </Button>
          <Text fontSize="sm" color="gray.600">
            {meta.page} / {meta.total_pages || 1}
          </Text>
          <Button
            variant="ghost"
            onClick={() =>
              loadActivity({
                ...filters,
                page: Math.min(Number(meta.total_pages || 1), Number(meta.page || 1) + 1),
              })
            }
            isDisabled={Number(meta.page || 1) >= Number(meta.total_pages || 1) || isLoading}
          >
            Next
          </Button>
        </HStack>
      </VmsPageCard>

      <Drawer isOpen={Boolean(selectedItem)} placement="right" onClose={() => setSelectedItem(null)} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">Activity Details</DrawerHeader>
          <DrawerBody>
            {!selectedItem ? null : (
              <VStack align="stretch" spacing={3} py={2}>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Action
                  </Text>
                  <Text fontWeight="700">{ACTION_LABELS[selectedItem.action] || selectedItem.action || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Time
                  </Text>
                  <Text>{formatDateTime(selectedItem.created_at)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Actor
                  </Text>
                  <Text>{getActorName(selectedItem.actor)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Role
                  </Text>
                  <Text>{getActorRole(selectedItem.actor)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Entity
                  </Text>
                  <Text>{[selectedItem.entity_type, selectedItem.entity_id].filter(Boolean).join(": ") || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500">
                    Request code
                  </Text>
                  <Text>{selectedItem.request_code || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    Raw details
                  </Text>
                  <Code display="block" whiteSpace="pre-wrap" p={3} borderRadius="10px">
                    {JSON.stringify(selectedItem.details || {}, null, 2)}
                  </Code>
                </Box>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </VStack>
  );
};

export default VmsActivityLog;
